/**
 * MeshTopologyDiagram -- the "Proposed topology" step of the Mesh page (OP#1803), and the same
 * view after deploy (expected vs observed).
 *
 * Hand-drawn SVG (no graph library, no CDN), laid out by the pure lib/meshTopology.buildDiagram:
 *   switch / port --solid cable--> wired root - - dashed expected link - -> mesh point
 * After deploy the links the APs REPORT are solid; an AP whose observed role or parent differs
 * from the plan is flagged. Plain language first (summary, checks by severity, push order); the
 * engineering detail per AP sits behind "Technical details" (same pattern as the readiness rows).
 *
 * Colours: Tailwind tokens only (bg-surface / surface-2, border-line, text-ink / ink-muted); the
 * status ramp (healthy / warn / critical / info) only for state. No brand colour here -- nothing
 * in the diagram is interactive. Light and dark come from the tokens. The SVG scales to the card
 * width (tablet-first) and scrolls sideways only when a large mesh needs it.
 */
import type { MeshProposedTopology } from "@/lib/api/siteMesh";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  BOX_W, type DiagramMode, type Tone, buildDiagram, checksBySeverity, severityTone, SEVERITY_TITLE,
} from "@/lib/meshTopology";
import { apLabel, fmtRssi } from "@/lib/meshView";
import { cn } from "@/lib/cn";

// Literal class names so Tailwind's JIT keeps them.
const STROKE: Record<Tone, string> = {
  healthy: "stroke-healthy",
  warn: "stroke-warn",
  critical: "stroke-critical",
  info: "stroke-info",
  unknown: "stroke-ink-subtle",
};
const FILL: Record<Tone, string> = {
  healthy: "fill-healthy",
  warn: "fill-warn",
  critical: "fill-critical",
  info: "fill-info",
  unknown: "fill-ink-subtle",
};
const PANEL: Record<"block" | "warn" | "info", string> = {
  block: "border-critical/40 bg-critical-soft text-critical",
  warn: "border-warn/40 bg-warn-soft text-warn",
  info: "border-info/40 bg-info-soft text-info",
};

function trim(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** The SVG itself. */
export function TopologySvg({ topo, mode }: { topo: MeshProposedTopology; mode: DiagramMode }) {
  const d = buildDiagram(topo, mode);
  const pos = new Map(d.boxes.map((b) => [b.id, b]));
  if (!d.boxes.length) return null;
  return (
    <div className="overflow-x-auto rounded border border-line bg-surface">
      <svg
        viewBox={`0 0 ${d.width} ${d.height}`}
        width="100%"
        style={{ minWidth: Math.min(d.width, 640), maxHeight: d.height }}
        role="img"
        aria-label={mode === "plan" ? "Proposed mesh topology" : "Mesh topology: observed and expected links"}
        className="text-ink"
      >
        {/* Lines first so boxes paint over their ends. */}
        {d.lines.map((l) => {
          const a = pos.get(l.from);
          const b = pos.get(l.to);
          if (!a || !b) return null;
          const x1 = a.x + a.w / 2;
          const y1 = a.y + a.h;
          const x2 = b.x + b.w / 2;
          const y2 = b.y;
          // Two lines into the same AP (observed + expected) are nudged apart.
          const nudge = l.kind === "expected" && d.lines.some((o) => o.kind === "observed" && o.to === l.to) ? 10 : 0;
          return (
            <g key={l.id} data-testid={`mesh-line-${l.kind}`}>
              <line
                x1={x1 + nudge}
                y1={y1}
                x2={x2 + nudge}
                y2={y2}
                className={STROKE[l.tone]}
                strokeWidth={l.kind === "cable" ? 2.5 : 2}
                strokeDasharray={l.dashed ? "6 5" : undefined}
                strokeOpacity={l.tone === "unknown" ? 0.7 : 1}
              />
              {l.label && (
                <text
                  x={(x1 + x2) / 2 + nudge + 6}
                  y={(y1 + y2) / 2 + (nudge ? 12 : 0)}
                  className="fill-ink-muted"
                  fontSize={10}
                >
                  {l.label}
                </text>
              )}
            </g>
          );
        })}
        {d.boxes.map((b) => (
          <g key={b.id} data-testid={`mesh-box-${b.serial ?? b.id}`}>
            <rect
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              rx={b.kind === "switch" ? 4 : 8}
              className={b.kind === "switch" ? "fill-surface-2" : "fill-surface"}
              stroke="currentColor"
              strokeOpacity={b.tone === "critical" || b.tone === "warn" ? 0 : 0.25}
            />
            {(b.tone === "critical" || b.tone === "warn") && (
              <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={8} fill="none" className={STROKE[b.tone]} strokeWidth={1.5} />
            )}
            {b.kind === "ap" && <circle cx={b.x + 14} cy={b.y + 17} r={4.5} className={FILL[b.tone]} />}
            <text x={b.x + (b.kind === "ap" ? 26 : 10)} y={b.y + 20} className="fill-ink" fontSize={12} fontWeight={500}>
              {trim(b.label, b.kind === "ap" ? 19 : 22)}
            </text>
            <text x={b.x + (b.kind === "ap" ? 26 : 10)} y={b.y + (b.kind === "ap" ? 36 : 33)} className="fill-ink-muted" fontSize={10}>
              {trim(b.sublabel, 26)}
            </text>
            {b.flag && (
              <text x={b.x + BOX_W / 2} y={b.y + b.h + 13} textAnchor="middle" className={FILL[b.tone === "unknown" ? "warn" : b.tone]} fontSize={10} fontWeight={600}>
                {b.flag}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

/** Legend: what solid / dashed mean in this mode. */
function Legend({ mode }: { mode: DiagramMode }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
      <span className="flex items-center gap-1">
        <svg width="28" height="8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" className="stroke-ink-subtle" strokeWidth={2.5} /></svg>
        cable (switch port)
      </span>
      {mode === "observed" && (
        <span className="flex items-center gap-1">
          <svg width="28" height="8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" className="stroke-healthy" strokeWidth={2} /></svg>
          mesh link the APs report
        </span>
      )}
      <span className="flex items-center gap-1">
        <svg width="28" height="8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" className="stroke-ink-subtle" strokeWidth={2} strokeDasharray="6 5" /></svg>
        expected mesh link
      </span>
      <span>Line colour = signal: good / fair / poor; grey = not measured yet.</span>
    </div>
  );
}

/** Checks grouped by severity, plain sentences. */
export function TopologyChecks({ topo }: { topo: MeshProposedTopology }) {
  const groups = checksBySeverity(topo.checks);
  return (
    <div className="flex flex-col gap-2" data-testid="mesh-topology-checks">
      {(["block", "warn", "info"] as const).map((sev) =>
        groups[sev].length ? (
          <div key={sev} role={sev === "block" ? "alert" : undefined} className={cn("rounded border p-3 text-sm", PANEL[sev])}>
            <div className="mb-1 flex items-center gap-2 font-medium">
              <StatusPill status={severityTone(sev)}>{sev === "block" ? "Blocks apply" : sev === "warn" ? "Warning" : "Note"}</StatusPill>
              {SEVERITY_TITLE[sev]}
            </div>
            <ul className="list-disc space-y-1 pl-5">
              {groups[sev].map((c, i) => (
                <li key={`${c.code}-${c.serial ?? ""}-${i}`}>{c.message}</li>
              ))}
            </ul>
          </div>
        ) : null,
      )}
    </div>
  );
}

/** Per-AP engineering detail (collapsed by default). */
function TechnicalDetails({ topo, mode }: { topo: MeshProposedTopology; mode: DiagramMode }) {
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-ink-subtle">Technical details</summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[40rem] text-left">
          <thead className="text-ink-muted">
            <tr className="border-b border-line">
              <th className="py-1 pr-3 font-medium">AP</th>
              <th className="py-1 pr-3 font-medium">Expected role / why</th>
              <th className="py-1 pr-3 font-medium">Uplink</th>
              <th className="py-1 pr-3 font-medium">Expected parent</th>
              {mode === "observed" && <th className="py-1 pr-3 font-medium">Observed</th>}
            </tr>
          </thead>
          <tbody className="text-ink">
            {topo.nodes.map((n) => (
              <tr key={n.serial} className="border-b border-line align-top">
                <td className="py-1 pr-3">
                  <div className="font-medium">{n.name}</div>
                  <div className="font-mono text-ink-subtle">{n.serial}</div>
                </td>
                <td className="py-1 pr-3">
                  <div>{n.expected_role}{n.override !== "auto" ? ` (override: ${n.override})` : ""}</div>
                  <div className="text-ink-muted">{n.role_reason}</div>
                </td>
                <td className="py-1 pr-3">
                  <div>{n.uplink.label}</div>
                  {(n.uplink.local_port || n.uplink.vlan != null) && (
                    <div className="text-ink-muted">
                      {n.uplink.local_port ? `AP port ${n.uplink.local_port}` : ""}
                      {n.uplink.vlan != null ? ` · native VLAN ${n.uplink.vlan}` : ""}
                    </div>
                  )}
                </td>
                <td className="py-1 pr-3">
                  {n.expected_role === "point" ? (
                    <>
                      <div>
                        {n.expected_parent_name ?? "none"} · hop {n.expected_hop_count ?? "—"} · {n.link_source}
                      </div>
                      {n.candidate_roots.length > 0 && (
                        <div className="text-ink-muted">
                          Roots in reach: {n.candidate_roots.map((c) => `${c.name} (${fmtRssi(c.rssi)})`).join(", ")}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-ink-muted">—</span>
                  )}
                </td>
                {mode === "observed" && (
                  <td className="py-1 pr-3">
                    <div>
                      {n.observed.role}
                      {n.observed.parent_name ? ` via ${n.observed.parent_name}` : ""} · {fmtRssi(n.observed.link_rssi)}
                    </div>
                    {n.differs.map((x) => (
                      <div key={x.field} className="text-warn">{x.message}</div>
                    ))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * The full step: summary line, diagram + legend, checks, left-out APs, push order, technical
 * details. `showPushOrder` is off for the post-deploy (status) view, which mints no order.
 */
export function MeshTopologyPanel({
  topo, mode, title, showPushOrder = true,
}: {
  topo: MeshProposedTopology;
  mode: DiagramMode;
  title: string;
  showPushOrder?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3" data-testid={`mesh-topology-${mode}`}>
      <div>
        <div className="text-sm font-medium text-ink">{title}</div>
        <p className="text-sm text-ink-muted">{topo.summary}</p>
      </div>
      {topo.enabled && <TopologySvg topo={topo} mode={mode} />}
      {topo.enabled && <Legend mode={mode} />}
      <TopologyChecks topo={topo} />
      {topo.excluded.length > 0 && (
        <div className="rounded border border-line bg-surface-2 p-3 text-sm">
          <div className="mb-1 font-medium text-ink">Left out of the mesh (stay wired-only)</div>
          <ul className="list-disc space-y-1 pl-5 text-ink-muted">
            {topo.excluded.map((x) => (
              <li key={x.serial}>
                <span className="font-medium text-ink">{apLabel(x)}</span> — {x.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showPushOrder && topo.push_order.length > 0 && (
        <div className="rounded border border-line bg-surface-2 p-3 text-sm">
          <div className="mb-1 font-medium text-ink">Push order (one AP at a time)</div>
          <ol className="flex flex-col gap-1">
            {topo.push_order.map((s) => (
              <li key={s.serial} className="flex flex-wrap items-center gap-2">
                <span className="w-5 text-ink-subtle">{s.step}.</span>
                <span className="text-ink">{apLabel(s)}</span>
                <span className="text-xs text-ink-muted">
                  {s.expected_role === "root" ? "wired root" : s.expected_role === "point" ? "mesh point" : "mesh removed"} · offline ~{s.outage_minutes} min
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
      <TechnicalDetails topo={topo} mode={mode} />
    </div>
  );
}
